import React, { useState, useEffect } from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import CancelIcon from "@mui/icons-material/Cancel";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import postService from "../../Services/post-service";
import { toast } from "react-toastify";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "#FFFFFF",
  boxShadow: 24,
  borderRadius: "16px",
  p: 4,
};

export default function IdentityDocModal({
  open,
  handleClose,
  setIdentityStatus,
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("NIN");
  const [fileSize, setFileSize] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setStep(1);
      setFile(null);
      setDocType("NIN");
      setUploadProgress(0);
      setFileSize(false);
      setErrorMsg("");
    }
  }, [open]);

  //  file size limit 5mb
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      if (e.target.files[0].size > MAX_FILE_SIZE) {
        setFileSize(true);
        setFile(null);
        return;
      }
      setFileSize(false);
      setFile(e.target.files[0]);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        const rawBase64 = fileReader.result.split(",")[1] || fileReader.result;
        resolve(rawBase64);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleUploadClick = async () => {
    if (file && !fileSize) {
      setStep(2);
      setErrorMsg("");
      setUploadProgress(20);

      try {
        const base64String = await convertToBase64(file);
        setUploadProgress(50);
        const response = await postService.uploadIdentityDocument(base64String, docType);
        if (response.data?.statusCode === 200) {
          setUploadProgress(100);
          setStep(3);
        } else {
          setStep(4);
          setErrorMsg(response.data?.description || "Uploading failed");
        }
      } catch (error) {
        setStep(4);
        setErrorMsg(error.response?.data?.description || "Uploading failed");
      }
    }
  };

  const handleDoneClick = () => {
    setIdentityStatus("Under review");
    handleClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  return (
    <Modal
      aria-labelledby="identity-verification-modal"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          {step === 1 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-[20px] font-semibold text-[#1C1B1F]">
                  Identity document verification
                </h2>
                <CancelIcon
                  onClick={handleClose}
                  className="text-[#1C1B1F] cursor-pointer"
                />
              </div>
              <p className="text-[#79747E] text-[14px] mb-6 leading-relaxed">
                Upload a valid government-issued ID. We accept National ID
                (NIN), International Passport, or Driver's License.
              </p>

              <div className="border-2 border-dashed border-[#00B172] rounded-[12px] p-6 text-center mb-6 bg-white relative">
                <input
                  type="file"
                  accept=".svg, .png, .jpg, .gif, .pdf"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex justify-center mb-2">
                  <div className="bg-[#F4F4F4] p-3 rounded-lg">
                    <CloudUploadOutlinedIcon className="text-[#323232]" />
                  </div>
                </div>
                <p className="text-[#1C1B1F] font-medium text-[14px]">
                  Click to upload or drag and drop
                </p>
                <p className="text-[#1C1B1F] text-[14px] mb-2">
                  or
                  <span className="text-[#00B172] cursor-pointer">
                    Browse files
                  </span>
                </p>
                <p className="text-[#79747E] text-[12px]">
                  SVG, PNG, JPG or GIF (max. 800x400px)
                </p>
                {file && (
                  <p className="text-[#00B172] text-[14px] font-medium mt-2">
                    Selected: {file.name}
                  </p>
                )}
              </div>
              {/* file size limit */}
              {fileSize && (
                <div className="bg-[#A844441A] rounded-lg p-2 flex gap-2 my-2">
                  <InfoOutlinedIcon
                    className="text-[#A84444]"
                    sx={{ fontSize: 18 }}
                  />
                  <div>
                    <p className="text-[#A84444] text-[12px] font-bold">
                      File exceeds the 5MB limit. Please upload a smaller file.
                    </p>
                  </div>
                </div>
              )}
              {/* upload error */}
              {/* {errorMsg && (
                <div className="bg-[#A844441A] rounded-lg p-2 flex gap-2 my-2">
                  <InfoOutlinedIcon
                    className="text-[#A84444]"
                    sx={{ fontSize: 18 }}
                  />
                  <div>
                    <p className="text-[#A84444] text-[12px] font-bold">
                      {errorMsg}
                    </p>
                  </div>
                </div>
              )} */}
              <div className="mb-6">
                <label className="block text-[#1C1B1F] font-medium text-[16px] mb-2">
                  Document Type
                </label>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="w-full bg-[#F4F4F4] border-none rounded-[8px] p-3 text-[14px] text-[#1C1B1F] outline-none"
                >
                  <option value="" disabled hidden>
                    Placeholder text
                  </option>
                  <option value="NIN">
                    National identification number(NIN)
                  </option>
                  <option value="International Passport">
                    International Passport
                  </option>
                  <option value="Drivers License">Drivers License</option>
                  <option value="Voters Card">Voters Card</option>
                </select>
              </div>

              <button
                onClick={handleUploadClick}
                disabled={!file}
                className={`w-full py-3 rounded-[8px] text-white font-medium text-[16px] transition-colors ${file ? "bg-[#00B172] hover:bg-[#009b63]" : "bg-[#a3e4cb] cursor-not-allowed"}`}
              >
                Upload document
              </button>

              <p className="text-center text-[#79747E] text-[12px] mt-4">
                Your data is encrypted and secure
              </p>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-[20px] font-semibold text-[#1C1B1F]">
                  Identity document verification
                </h2>
                <CancelIcon
                  onClick={handleClose}
                  className="text-[#1C1B1F] cursor-pointer"
                />
              </div>
              <p className="text-[#79747E] text-[14px] mb-6">
                Uploading your document, please wait....
              </p>

              <div className="border border-[#E6E6E6] rounded-[12px] p-4 flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <InsertDriveFileOutlinedIcon className="text-[#00B172]" />
                  <div>
                    <p className="text-[#1C1B1F] font-semibold text-[14px]">
                      {file?.name || "Document.pdf"}
                    </p>
                    <p className="text-[#79747E] text-[12px]">
                      {file ? formatFileSize(file.size) : "2.4 MB"}
                    </p>
                  </div>
                </div>
                {/* uploading spinner  */}
                <div className="w-6 h-6 border-2 border-[#E6E6E6] border-t-[#00B172] rounded-full animate-spin"></div>
              </div>

              <div className="mb-8 hidden">
                <div className="flex justify-between text-[12px] text-[#1C1B1F] font-medium mb-2">
                  <span>Uploading....</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-[#E6E6E6] rounded-full h-2">
                  <div
                    className="bg-[#00B172] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
              {/* 
              <button
                disabled
                className="w-full py-3 rounded-[8px] bg-[#00B172] text-white font-medium text-[16px] opacity-80 cursor-not-allowed"
              >
                Upload document
              </button> */}
              <p className="text-center text-[#79747E] text-[12px] mt-4">
                Your data is encrypted and secure
              </p>
            </div>
          )}
          {step === 4 && (
            <div className="text-center py-4 relative">
              <div className="absolute top-[-16px] right-[-16px]">
                <CancelIcon
                  onClick={handleClose}
                  className="text-[#1C1B1F] cursor-pointer"
                />
              </div>

              <div className="flex justify-center mb-4 mt-4">
                <div className="bg-[#FAF2F2] w-[80px] h-[80px] rounded-full flex items-center justify-center">
                  <ErrorOutlineIcon className="text-[#A84444] text-[32px]" />
                </div>
              </div>

              <div className="bg-[#FAF2F2] text-[#A84444] text-[14px] font-medium px-4 py-1.5 rounded-full inline-block mb-6">
                Rejected
              </div>

              <h2 className="text-[18px] font-semibold text-[#A84444] mb-2">
                Verification Failed
              </h2>
              <p className="text-[#1C1B1F] text-[14px] leading-relaxed mb-1">
                We could not verify your document.
              </p>
              <p className="text-[#1C1B1F] text-[14px] leading-relaxed mb-8 max-w-[300px] mx-auto">
                Reason: {errorMsg}
              </p>

              <button
                onClick={() => setStep(1)}
                className="w-full py-3 rounded-[8px] bg-[#00B172] hover:bg-[#009b63] text-white font-medium text-[16px] transition-colors"
              >
                Resubmit documnet
              </button>
            </div>
          )}
          {step === 3 && (
            <div className="text-center py-4 relative">
              <div className="absolute top-[-16px] right-[-16px]">
                <CancelIcon
                  onClick={handleClose}
                  className="text-[#1C1B1F] cursor-pointer"
                />
              </div>

              <div className="flex justify-center mb-4">
                <div className="bg-[#FFF4D2] w-[80px] h-[80px] rounded-full flex items-center justify-center">
                  <AccessTimeIcon className="text-[#B59124] text-[32px]" />
                </div>
              </div>

              <div className="bg-[#FFF4D2] text-[#B59124] text-[14px] font-medium px-4 py-1.5 rounded-full inline-block mb-6">
                Under review
              </div>

              <h2 className="text-[18px] font-semibold text-[#1C1B1F] mb-2">
                Document Submitted
              </h2>
              <p className="text-[#79747E] text-[14px] leading-relaxed mb-8 max-w-[300px] mx-auto">
                Your identity document is under review. this usually takes 1-2
                business days. we'll notify you once it's done
              </p>

              <button
                onClick={handleDoneClick}
                className="w-full py-3 rounded-[8px] bg-[#00B172] hover:bg-[#009b63] text-white font-medium text-[16px] transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
